/**
 * <SEO /> — YADA's per-page <head> injector.
 *
 * Wraps react-helmet-async's <Helmet> with YADA's defaults so each page just
 * specifies its title + description and gets a complete <head> for free:
 *   <title> + meta description + meta keywords + OG + Twitter Card +
 *   canonical + hreflang × 3 + JSON-LD + optional robots noindex
 *
 * Usage:
 *   <SEO
 *     title={t('seo.aboutTitle')}
 *     description={t('seo.aboutDescription')}
 *     keywords={t('seo.aboutKeywords')}
 *     jsonLd={[buildOrganization(), buildBreadcrumbList([...])]}
 *   />
 */

import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import {
  buildAlternateUrl,
  buildCanonicalUrl,
  DEFAULT_KEYWORDS,
  DEFAULT_LOCALE,
  DEFAULT_OG_IMAGE,
  LOCALES,
  LOCALE_HREFLANG,
  OG_IMAGES,
  SITE_NAME,
  SITE_URL,
  TWITTER_SITE_HANDLE,
} from './siteMeta'
import {
  DESCRIPTION_MAX,
  TITLE_MAX,
  trimToLength,
} from './seoText'

export type SeoType = 'website' | 'article'

export interface SeoProps {
  /** Page title (without the site-name suffix). */
  title: string
  /** 1-2 sentence description for SERP / OG / Twitter. */
  description: string
  /**
   * Comma-separated keywords for `<meta name="keywords">`. Google ignores
   * keywords but Baidu still partially uses them, so this matters for the
   * zh-Hans (mainland China) audience. If omitted, the page falls back to
   * `DEFAULT_KEYWORDS`.
   */
  keywords?: string
  /** Path under SITE_URL — defaults to current `window.location.pathname`. */
  path?: string
  /** Override the OG image (absolute path under SITE_URL or full URL). */
  ogImage?: string
  /**
   * og:type — 'website' for most pages, 'article' for content detail pages
   * (lecture, article, magazine detail).
   */
  type?: SeoType
  /** Set true for soft-404 / noindex routes. */
  noindex?: boolean
  /** Per-page Twitter @-handle for `twitter:creator`. */
  twitterCreator?: string
  /**
   * Article publication date in ISO 8601. When set, adds `article:published_time`
   * to OG and `datePublished` to Article JSON-LD-style OG tags.
   */
  articlePublishedTime?: string
  /** Article author display name for `article:author`. */
  articleAuthor?: string
  /** One or more JSON-LD payloads to embed as <script type="application/ld+json">. */
  jsonLd?: ReadonlyArray<Record<string, unknown>> | Record<string, unknown>
}

function resolveOgImage(ogImage: string | undefined): string {
  const raw = ogImage ?? DEFAULT_OG_IMAGE
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw
  return `${SITE_URL}${raw.startsWith('/') ? raw : `/${raw}`}`
}

/**
 * The brand appended to every `<title>`.
 *
 * English pages get the short brand: the Chinese organisation name in an
 * English SERP is out of place, and it costs 8 characters of the title
 * budget. Most English titles already contain "YADA", in which case the
 * suffix is not appended at all.
 */
function brandForPath(pathname: string | undefined): string {
  if (pathname && /^\/en(\/|$)/.test(pathname)) return 'YADA'
  return SITE_NAME
}

/**
 * `useLocation` wrapped in a try/catch so `<SEO>` does not crash if rendered
 * outside a React Router context (legacy admin subtrees, isolated test
 * mounts). Returns `null` when no router is available; the canonical fallback
 * then uses `window.location.pathname` in the browser or `'/'` in SSR.
 */
function useRouterLocationSafe(): { pathname: string } | null {
  try {
    return useLocation()
  } catch {
    return null
  }
}

export default function SEO({
  title,
  description,
  keywords,
  path,
  ogImage,
  type = 'website',
  noindex = false,
  twitterCreator,
  articlePublishedTime,
  articleAuthor,
  jsonLd,
}: SeoProps) {
  // Resolve canonical path. Precedence: explicit `path` prop → React Router's
  // useLocation() (works in both CSR and SSR via MemoryRouter) → window fallback
  // (only when no Router is available, e.g. legacy admin subtrees). This means
  // page components no longer need to remember to pass `path` — the prerender
  // pass (MemoryRouter initialEntries={['/about']}) just works.
  //
  // ⚠️ DO NOT pass `path` from a page component. Since Phase 6 path-based
  // locales, the router pathname is already the fully-qualified URL
  // (`/en/articles/foo`). A hardcoded `path="/articles/foo"` silently drops the
  // locale segment, so /en/, /zh-Hant/ and /zh-Hans/ all canonicalised to the
  // same phantom URL that appeared in no sitemap — and Google then picked the
  // homepage instead. This precedence order survives only for the legacy admin
  // subtrees, which render <SEO> without a router.
  //
  // useLocation throws if used outside a Router; swallow that case so admin
  // pages that render <SEO> without a router don't crash.
  const routerLocation = useRouterLocationSafe()
  const canonicalPath =
    path ??
    routerLocation?.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '/')
  const canonicalUrl = buildCanonicalUrl(canonicalPath)

  const ogImg = resolveOgImage(ogImage)
  const ogLocale = LOCALE_HREFLANG[DEFAULT_LOCALE]

  // ── Title / description length policy ────────────────────────────────────
  //
  // The SEO audit (scripts/audit-deployed-seo.mts) checks title ≤ 80 chars
  // and description ≤ 180 — the range a search engine actually displays.
  // Anything longer is truncated by Google anyway, so the honest thing is to
  // bound it ourselves.
  //
  // Two sources of length:
  //   - hand-written copy (the i18n files), which is written to fit;
  //   - CMS copy (article meta_title / meta_description, magazine summary),
  //     which the operator writes freely and which can run to 380+ chars.
  //
  // The trim is the safety net for the second; it is a no-op for anything
  // already within the policy.
  const brand = brandForPath(routerLocation?.pathname)
  const titleSuffix = title.includes(brand) ? '' : ` | ${brand}`
  const fullTitle = trimToLength(title, TITLE_MAX - titleSuffix.length) + titleSuffix
  const metaDescription = trimToLength(description, DESCRIPTION_MAX)

  // Normalise jsonLd into an array so we always render the same shape.
  const jsonLdArray: ReadonlyArray<Record<string, unknown>> = Array.isArray(jsonLd)
    ? jsonLd
    : jsonLd
      ? [jsonLd]
      : []

  // Per-page keywords take precedence; fall back to the YADA-wide set so we
  // never emit an empty `<meta name="keywords">`.
  const resolvedKeywords = keywords?.trim() || DEFAULT_KEYWORDS

  // Twitter:site is only emitted when we actually have a handle — Twitter
  // throws a warning otherwise.
  const twitterSite = TWITTER_SITE_HANDLE ? `${TWITTER_SITE_HANDLE}` : null

  return (
    <Helmet prioritizeSeoTags>
      {/* Title + meta description + keywords */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={resolvedKeywords} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang — three locales + x-default */}
      {LOCALES.map((locale) => (
        <link
          key={locale}
          rel="alternate"
          hrefLang={LOCALE_HREFLANG[locale]}
          href={buildAlternateUrl(locale, canonicalPath)}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={buildAlternateUrl(DEFAULT_LOCALE, canonicalPath)}
      />

      {/* Open Graph — base */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={ogLocale} />
      {LOCALES.filter((l) => l !== DEFAULT_LOCALE).map((locale) => (
        <meta
          key={locale}
          property="og:locale:alternate"
          content={LOCALE_HREFLANG[locale]}
        />
      ))}

      {/* Open Graph — image variants (WebP first, PNG fallback). Crawlers
          pick the first one they support. Multiple tags are spec-allowed. */}
      {OG_IMAGES.map((img) => (
        <meta key={img.url} property="og:image" content={`${SITE_URL}${img.url}`} />
      ))}
      {OG_IMAGES.map((img) => (
        <meta key={`${img.url}-w`} property="og:image:width" content={String(img.width)} />
      ))}
      {OG_IMAGES.map((img) => (
        <meta key={`${img.url}-h`} property="og:image:height" content={String(img.height)} />
      ))}
      {OG_IMAGES.map((img) => (
        <meta key={`${img.url}-t`} property="og:image:type" content={img.type} />
      ))}
      <meta
        property="og:image:alt"
        content="青年耀進發展協會 YADA · 教育傳媒"
      />
      {/* Per-page OG image override (if provided) is added LAST so crawlers
          that respect order see the per-page image first. */}
      {ogImage && <meta property="og:image" content={ogImg} />}

      {/* Open Graph — article-specific (only when type === 'article') */}
      {type === 'article' && articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {type === 'article' && articleAuthor && (
        <meta property="article:author" content={articleAuthor} />
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImg} />
      <meta
        name="twitter:image:alt"
        content="青年耀進發展協會 YADA · 教育傳媒"
      />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      {twitterCreator && <meta name="twitter:creator" content={twitterCreator} />}

      {/* JSON-LD — one <script> per payload so the validator can parse each
          independently. We escape `<` to its unicode escape so that any
          </script>-shaped substring inside a translatable string cannot
          break out of the <script> tag. JSON.stringify alone does not do this. */}
      {jsonLdArray.map((payload, i) => (
        <script
          key={`jsonld-${i}`}
          type="application/ld+json"
          // JSON-LD is generated from our own static builders (structuredData.ts);
          // string values are still HTML-escaped here as defence in depth.
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(payload).replace(/</g, '\\u003c'),
          }}
        />
      ))}
    </Helmet>
  )
}
